/** DART 공시 XBRL 열람 뷰 — 읽기 전용 */

import { useEffect, useMemo, useRef } from "react";

import {
  IxdPreviewRenderer,
  type IxdPreviewRendererRef,
} from "@/components/ixd-preview";
import { useIxdEditorStore } from "./store";
import { SegmentControl } from "@/toggle-ui-kit/segment-control";
import type { FsType, IXDPreviewResponse } from "@/apis/ixd-editor/types";
import { EditorSidebar } from "./editor-sidebar";

const FS_TYPE_ITEMS = [
  { value: "consolidated" as FsType, label: "연결" },
  { value: "separate" as FsType, label: "별도" },
];

/**
 * EditorContentView 를 재사용하지 않는다 — 그쪽은 저장/다운로드 mutation 을 가진
 * 편집 세션(useIxdEditSession)에 묶여 있고, 공시본에는 그 경로가 아예 없다.
 * 세션을 통과시키려고 자리표시자 reportId 를 넘기면 편집 UI 가 살아 있는 채로
 * 서버에서만 막히게 된다.
 */
export const FilingContentView = ({
  previewData,
  filename,
}: {
  previewData: IXDPreviewResponse;
  filename: string | null;
}) => {
  const selectedFsType = useIxdEditorStore((s) => s.selectedFsType);
  const setSelectedFsType = useIxdEditorStore((s) => s.setSelectedFsType);
  const activeChunkId = useIxdEditorStore((s) => s.activeChunkId);
  const setActiveChunkId = useIxdEditorStore((s) => s.setActiveChunkId);
  const rendererRef = useRef<IxdPreviewRendererRef>(null);

  const chunks = useMemo(
    () =>
      selectedFsType === "consolidated"
        ? previewData.consolidatedChunks
        : previewData.separateChunks,
    [previewData, selectedFsType],
  );

  useEffect(() => {
    if (chunks.length > 0 && !activeChunkId) {
      setActiveChunkId(chunks[0].id);
    }
  }, [chunks, activeChunkId, setActiveChunkId]);

  return (
    <div className="relative flex flex-col h-full w-full">
      <header className="flex items-center justify-between gap-4 border-b border-line-normal px-4 py-2">
        <div className="flex min-w-0 items-center gap-2">
          <span className="label-2-semibold shrink-0 rounded bg-background-neutral-alternative px-2 py-0.5 text-label-neutral">
            공시 XBRL · 읽기 전용
          </span>
          {filename && (
            <span className="body-2-normal-medium truncate text-label-normal">
              {filename}
            </span>
          )}
        </div>
        <SegmentControl
          items={FS_TYPE_ITEMS}
          value={selectedFsType}
          onChange={setSelectedFsType}
          size="small"
        />
      </header>
      <div className="flex flex-1 min-h-0">
        <EditorSidebar
          chunks={chunks}
          activeChunkId={activeChunkId}
          onChunkClick={(chunkId) => {
            setActiveChunkId(chunkId);
            rendererRef.current?.scrollToChunk(chunkId);
          }}
        />
        <div className="flex-1 min-w-0 overflow-hidden">
          <IxdPreviewRenderer
            ref={rendererRef}
            chunks={chunks}
            selectedChunkId={activeChunkId}
          />
        </div>
      </div>
    </div>
  );
};
